import { Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import React, { ReactNode, useRef } from 'react';
import { NewCentralFormType } from '../types';
import { SearchEntityEnum } from './search-v3-types';
import { getAllValidSearchSpaces } from './search-v3-utils';

const mapIEOperation = {
  include: 'Omezit na: ',
  exclude: 'Vynechat: ',
};

const getLogicOperationNode = (negation?: boolean) => (
  <span style={{ color: '#cf2931' }}>{negation ? 'a není' : 'a současně'}</span>
);

export function SearchSummary({ pageCount }: { pageCount: number }) {
  const { values: orgVals, isSubmitting, submitCount } = useFormikContext<
    NewCentralFormType
  >();

  const previousCount = useRef(0);
  const prevVals = useRef(orgVals);

  if (isSubmitting || submitCount < 1 || pageCount < 0) {
    return null;
  }

  if (previousCount.current === submitCount) {
    //return null;
  } else {
    previousCount.current = submitCount;
    prevVals.current = orgVals;
  }

  const values = prevVals.current;

  //const values = orgVals;
  //console.log(values);
  const suffixMap: Record<string, string> = {
    COLLECTIONS: 'hledá se jen v popisu fondů',
    AID: 'hledá se jen v úvodu archivní pomůcky',
  };

  const renderSuffix = (id: string) =>
    suffixMap[id] ? (
      <>
        {' '}
        (<u>{suffixMap[id]}</u>)
      </>
    ) : null;

  const renderValue = (value: ReactNode) => <b>{value}</b>;

  const result: ReactNode[] = [];

  values.specificFilterRows.forEach((row, index) => {
    if (index > 0) {
      result.push(getLogicOperationNode(row.negation));
    }

    const { filterType, category, label } = row.filterOption;
    const suffix = renderSuffix(category.id);

    const pushRow = (value: ReactNode) =>
      result.push(
        <span>
          {label}: {renderValue(value)}
          {suffix}
        </span>
      );

    switch (filterType) {
      case 'FTX':
        pushRow(row.ftxFilterValue?.value ?? '');
        break;

      case 'TEXT':
        pushRow(row.textFilterValue?.value ?? '');
        break;

      case 'TIME_RANGE':
        pushRow(
          `${
            row.timeRangeFilterValue?.from
              ? new Date(row.timeRangeFilterValue.from).toLocaleDateString('cs')
              : ''
          } - ${
            row.timeRangeFilterValue?.to
              ? new Date(row.timeRangeFilterValue.to).toLocaleDateString('cs')
              : ''
          }`
        );
        break;

      case 'COMPLEX_DATE':
        pushRow(
          `${
            row.complexDateFilterValue?.from
              ? new Date(row.complexDateFilterValue.from).toLocaleDateString(
                  'cs'
                )
              : ''
          } - ${
            row.complexDateFilterValue?.to
              ? new Date(row.complexDateFilterValue.to).toLocaleDateString('cs')
              : ''
          }`
        );
        break;

      case 'CUSTOM':
        pushRow(
          <>
            {row.customFilterValues?.[0]?.label}
            {row.customFilterValues?.[0]?.label?.trim?.() ? ' = ' : ''}
            {row.filterOption?.getCustomFieldValueLabel
              ? row.filterOption.getCustomFieldValueLabel(
                  row.customFilterValues?.[1]?.value
                )
              : row.customFilterValues?.[1]?.value}
          </>
        );
        break;

      case 'COMPLEX_TEXT':
        pushRow(row.complexTextFilterValue?.value ?? '');
        break;

      case 'RELATION':
        pushRow(
          <>
            {row.relationFilterValue?.originator?.label ?? ''} ={' '}
            {row.relationFilterValue?.relationRole?.label ?? ''}
          </>
        );
        break;

      case 'RELATION_V2':
        pushRow(
          <>
            Heslo = {row.relationFilterValue?.originator?.label ?? ''}; Vztah ={' '}
            {row.relationFilterValue?.relationRole?.label ?? ''}
          </>
        );
        break;

      case 'REGISTER_CLASSES':
        pushRow(
          <>
            Třída = {row.registerClassesFilterValue?.class?.label}; Podtřída ={' '}
            {row.registerClassesFilterValue?.subClass?.label}
          </>
        );
        break;

      default:
        break;
    }
  });

  const sp = getAllValidSearchSpaces(values);
  const selectedLists: NewCentralFormType['selectedAids'][] = [];

  if (sp.includes(SearchEntityEnum.EAD_NODE_UNIT)) {
    selectedLists.push(values.selectedEvidenceUnitTypes);
  }
  if (sp.includes(SearchEntityEnum.ARCHIVAL_COLLECTION)) {
    selectedLists.push(values.selectedCollections);
  }

  if (sp.includes(SearchEntityEnum.ARCHIVAL_AID)) {
    selectedLists.push(values.selectedAids);
  }

  selectedLists.forEach((selectedList) => {
    if (selectedList.items.length > 0) {
      result.push(getLogicOperationNode(false));
      const list: ReactNode[] = [];
      list.push(mapIEOperation[selectedList.operation]);
      list.push(
        selectedList.items.map((item, index, all) => {
          return (
            <b>
              {item.label}
              {index !== all.length - 1 && ', '}
            </b>
          );
        })
      );

      result.push(list);
    }
  });

  return (
    <div
      style={{
        position: 'sticky',
        width: '100%',
        top: 81,
        left: 0,
        right: 0,
        maxHeight: 100,
        backgroundColor: 'rgb(255 244 244)',
        borderBottom: '1px solid #d3333340',
        padding: '10px',
        zIndex: 99999, // Ensure it's on top of other content
        boxShadow: '0 1px 3px 0px #00000049',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontWeight: 'normal',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        <Typography variant="body2">Aktivní filtry: </Typography>
        {result.map((item, index) => (
          <div
            key={index}
            style={{
              marginRight: '4px',
              marginLeft: '4px',
              border: '1px solid #ccc',
              padding: '0px 4px',
            }}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
